import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import {
  BUTTON_ACCENT_CLASSES,
  BUTTON_BASE_CLASSES,
  BUTTON_CAUTION_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  BUTTON_PRIMARY_CLASSES,
  BUTTON_SECONDARY_CLASSES,
  BUTTON_SUCCESS_CLASSES,
  BUTTON_TERTIARY_CLASSES,
  BUTTON_TOGGLE_CLASSES,
} from "./helpers";

import type { PolymorphicRef } from "types";

export const ROUNDED_BUTTON_KINDS = [
  "primary",
  "secondary",
  "tertiary",
  "accent",
  "success",
  "caution",
  "toggle",
] as const;

export type RoundedButtonKind = (typeof ROUNDED_BUTTON_KINDS)[number];

// NOTE: [&>*]:!gap-x is needed to override any gap class that might be applied by the child component, such as IconText.
// Similarly, [&_svg]:!size-x is needed to override any size class that might be applied by a child icon.
const COMMON_CLASSES = twMerge(
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  "text-sm rounded-3xl border px-4 py-3 h-[46px] gap-2 [&>*]:!gap-2 [&_svg]:!size-4",
);

const KIND_CLASSES: Record<RoundedButtonKind, string> = {
  primary: BUTTON_PRIMARY_CLASSES,

  secondary: BUTTON_SECONDARY_CLASSES,

  tertiary: BUTTON_TERTIARY_CLASSES,

  accent: BUTTON_ACCENT_CLASSES,

  success: BUTTON_SUCCESS_CLASSES,

  caution: BUTTON_CAUTION_CLASSES,

  toggle: BUTTON_TOGGLE_CLASSES,
};

export type RoundedButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  isSelected?: boolean;
  kind: RoundedButtonKind;
};

/**
 * `RoundedButton` is a button with rounded corners.
 * It is a polymorphic component, meaning it can be rendered as any HTML element.
 * By default, it is rendered as a <button> element, but you can change it to any other element by passing the `as` prop.
 *
 * To render a `RoundedButton` as a link, combine it with the `Link` component from react-router-dom, like this:
 * ```tsx
 * <RoundedButton as={Link} to="/somewhere">
 *   This looks like a button, but it's a link!
 * </RoundedButton>
 * ```
 */
export const RoundedButton = forwardRef<
  HTMLButtonElement,
  Omit<RoundedButtonProps<"button">, "ref">
>(function RoundedButtonInternal(
  { isSelected, kind, className, ...rest }: RoundedButtonProps<"button">,
  ref,
) {
  return (
    <BaseButton
      className={twMerge(COMMON_CLASSES, KIND_CLASSES[kind], className)}
      isSelected={isSelected}
      ref={ref}
      {...rest}
    />
  );
}) as <T extends React.ElementType = "button">(
  props: RoundedButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
